import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/store/app.store';
import { updateLanguageTranslations } from 'src/app/store/actions/language.actions';
import { Observable, firstValueFrom, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService
  ) {}

  /**
   * Force reload and update translations immediately
   * @returns Promise that resolves when translations are loaded
   */
  async updateTranslations(): Promise<void> {
    // Get current language
    const currentLang = this.translateService.currentLang || 'en';
    
    // Force reload language file
    await firstValueFrom(this.translateService.reloadLang(currentLang));
    
    // Get all translation keys
    const translationKeys = [
      'COMMON',
      'LOGIN',
      'MENU',
      'NAVIGATION',
      'AUTH',
      'DASHBOARD',
      'ANALYTICS',
      'WHITELIST',
      'SETTINGS',
      'CAMERA_RECORDS',
      'TITLES',
      'GLOBAL',
      'FILTERS',
      'AVAILABLE',
      'TRANSACTIONS',
      'BUTTONS',
      'TYPES',
      // Add more translation key groups as needed
    ];

    // Create translations object
    const translations: { [key: string]: any } = {};
    
    // Create an array of observables for each key
    const translationObservables = translationKeys.map(key => 
      this.translateService.get(key).pipe(
        map(translation => ({ key, translation }))
      )
    );
    
    // Wait for all translations to be fetched
    const results = await firstValueFrom(forkJoin(translationObservables));
    
    // Fill translations object
    results.forEach(({ key, translation }) => {
      translations[key] = translation;
    });
    
    // Update store with all translations at once
    this.store.dispatch(updateLanguageTranslations({ translations }));
  }

  // Helper method to get a specific translation
  getTranslation(key: string): Observable<string> {
    return this.store.select(state => state.language.translations).pipe(
      map(translations => {
        const parts = key.split('.');
        let result = translations;
        for (const part of parts) {
          if (result && result[part]) {
            result = result[part];
          } else {
            return key; // Return key if translation not found
          }
        }
        return typeof result === 'string' ? result : key;
      })
    );
  }

  /**
   * Initialize language service with forced reload
   * @returns Promise that resolves when initialization is complete
   */
  async initialize(): Promise<void> {
    // First, immediately load translations
    await this.updateTranslations();
    
    // Then set up listener for language changes
    this.translateService.onLangChange.subscribe(() => {
      this.updateTranslations();
    });
  }
} 